import { useMemo, useState } from "react";
import { DashboardLayout } from "../Dashboard";
import TableWithSearch from "@/components/TableWithSearch";
import { useParams } from "react-router";
import { reportHeaders } from "@/utils/tablesData";
import {
  formatOrganizationTimezoneDisplay,
  getErrToast,
  getPatientContextTimezoneDisplay,
  getReportDateRange,
  type ReportDatePreset,
} from "@/utils/helpers";
import AxiosInstance from "@/utils/AxiosInstance";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ColumnHeader } from "@/components/MyTable";
import type { DateRange } from "react-day-picker";
import { isAfter, startOfDay } from "date-fns";
import { HoverTooltip } from "@/components/ui/tooltip";
import { Monitor } from "lucide-react"; 

const PRESET_OPTIONS: { value: ReportDatePreset; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last7", label: "Last 7 Days" },
  { value: "last15", label: "Last 15 Days" },
  { value: "last30", label: "Last 30 Days" },
  { value: "last90", label: "Last 90 Days" },
  { value: "custom", label: "Custom date range" },
];

const ReportPage = () => {
  const [enrollmentData, setEnrollmentData] = useState<any[]>([]);
  const [criticalData, setCriticalData] = useState<any[]>([]);
  const [datePreset, setDatePreset] = useState<ReportDatePreset>("last30");
  /** Applied range used for API queries */
  const [customRange, setCustomRange] = useState<DateRange | undefined>();
  /** Draft range edited inside the popover; only applied when popover closes */
  const [draftCustomRange, setDraftCustomRange] = useState<DateRange | undefined>();
  const [customPickerOpen, setCustomPickerOpen] = useState(false);
  const [activeRangeBoundary, setActiveRangeBoundary] = useState<
    "from" | "to" | null
  >(null);

  const { startDate, endDate } = useMemo(
    () =>
      getReportDateRange(
        datePreset,
        customRange?.from,
        customRange?.to ?? customRange?.from,
      ),
    [datePreset, customRange],
  );

  const { id } = useParams();
  /** `/organization/all/reports` → do not send org filter to list/export APIs */
  const omitOrganizationParam =
    !id || String(id).toLowerCase() === "all";

  const [enrollmentFilters, setEnrollmentFilters] = useState<{
    patientType?: string;
  }>({ patientType: "all" });

  const [criticalFilters, setCriticalFilters] = useState<{
    status?: string[];
    patientType?: string;
  }>({patientType: "all"});

  const enrollmentFilterOptions = [
    {
      label: "Patient Type",
      key: "patientType",
      isMulti: false,
      values: [
        { label: "All", key: "all" },
        { label: "Outpatient", key: "outpatient" },
        { label: "Facility", key: "facility" },
      ],
    },
  ];

  const criticalFilterOptions = [
    {
      label: "Patient Type",
      key: "patientType",
      isMulti: false,
      values: [
        { label: "All", key: "all" },
        { label: "Outpatient", key: "outpatient" },
        { label: "Facility", key: "facility" },
      ],
    },
    {
      label: "Status",
      key: "status",
      isMulti: true,
      values: [
        { label: "Resolved", key: "resolved" },
        { label: "Invalid", key: "invalid" },
        { label: "Pending", key: "pending" },
        { label: "Dismissed", key: "dismissed" },
      ],
    },
  ];

  const handleEnrollmentExport = async (ctx?: { search?: string }) => {
    if (id == null || id === "") return;
    const t = toast.loading("Preparing export...");
    try {
      const api = AxiosInstance();
      const { data } = await api.get<Blob>("/patients/enrollment-report/export", {
        params: {
          ...(omitOrganizationParam ? {} : { organizationId: id }),
          status: "",
          monitoringType: "",
          startDate,
          endDate,
          search: ctx?.search?.trim() ?? "",
        },
        responseType: "blob",
      });

      if (data.type.includes("application/json")) {
        const text = await data.text();
        const rows = JSON.parse(text);
        const safeRows = Array.isArray(rows?.data?.rows) ? rows?.data?.rows : [];

        const ws = XLSX.utils.json_to_sheet(safeRows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Enrolment Report");
        XLSX.writeFile(wb, "enrollment_report.xlsx");
      } else {
        const url = window.URL.createObjectURL(data);
        const a = document.createElement("a");
        a.href = url;
        a.download = "enrollment_report.xlsx";
        a.click();
        window.URL.revokeObjectURL(url);
      }
      toast.success("Export downloaded successfully", { id: t });
    } catch (err: any) {
      toast.dismiss(t);
      getErrToast(err);
    }
  };

  const handleCriticalExport = async (ctx?: { search?: string }) => {
    if (id == null || id === "") return;
    const selectedStatuses = criticalFilters.status ?? [];
    const statusParam =
      selectedStatuses.length > 0
        ? selectedStatuses.join(",")
        : "";
    const patientType = String(criticalFilters.patientType ?? "").trim();
    const typeOfPatient =
      patientType && patientType !== "all" ? patientType : undefined;
    const t = toast.loading("Preparing export...");
    try {
      const api = AxiosInstance();
      const { data } = await api.get<Blob>("/critical-readings/report/export", {
        params: {
          ...(omitOrganizationParam ? {} : { organizationId: id }),
          status: statusParam,
          startDate,
          endDate,
          search: ctx?.search?.trim() ?? "",
          ...(typeOfPatient ? { typeOfPatient } : {}),
        },
        responseType: "blob",
      });

      if (data.type.includes("application/json")) {
        const text = await data.text();
        const rows = JSON.parse(text);
        const safeRows = Array.isArray(rows?.data?.rows) ? rows?.data?.rows : [];

        const ws = XLSX.utils.json_to_sheet(safeRows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Critical Report");
        XLSX.writeFile(wb, "critical_report.xlsx");
      } else {
        const url = window.URL.createObjectURL(data);
        const a = document.createElement("a");
        a.href = url;
        a.download = "critical_report.xlsx";
        a.click();
        window.URL.revokeObjectURL(url);
      }
      toast.success("Export downloaded successfully", { id: t });
    } catch (err: any) {
      toast.dismiss(t);
      getErrToast(err);
    }
  };

  /** Matches `/critical-readings/report` row shape (`data.rows`). */
  const criticalReportHeaders: ColumnHeader[] = [
    { id: "Patient ID", label: "Patient ID", isSortable: false },
    { id: "Patient Name", label: "Patient Name", isSortable: false },
    { id: "Organization", label: "Organization", isSortable: false },
    { id: "Address", label: "Address", isSortable: false },
    { id: "typeOfPatient", label: "Patient Type", isSortable: false },
    { id: "Timezone", label: "Timezone", isSortable: false },
    { id: "Phone Number", label: "Phone Number", isSortable: false },
    { id: "Email", label: "Email", isSortable: false },
    { id: "Device", label: "Device", isSortable: false },
    { id: "Reading", label: "Reading", isSortable: false },
    { id: "Cleared by", label: "Cleared by", isSortable: false },
    { id: "createdAt", label: "Received At", isSortable: true },
    { id: "Resolved At", label: "Resolved At", isSortable: true },
    { id: "Resolved Status", label: "Resolved Status", isSortable: false },
    { id: "facilityName", label: "Facility Name", isSortable: false },
    // { id: "deviceNames", label: "Devices", isSortable: false },
  ];

  const resetAllReportFilters = () => {
    setDatePreset("last30");
    setCustomRange(undefined);
    setDraftCustomRange(undefined);
    setCustomPickerOpen(false);
    setActiveRangeBoundary(null);
    setEnrollmentFilters({});
    setCriticalFilters({ status: [] });
  };

  const isFutureCalendarDay = (d: Date) =>
    isAfter(startOfDay(d), startOfDay(new Date()));

  const handleCustomRangeSelect = (_next: DateRange | undefined, day?: Date) => {
    if (!day || isFutureCalendarDay(day)) return;

    setDraftCustomRange((prev) => {
      const from = prev?.from;
      const to = prev?.to;

      // Start new range
      if (!from || !to) {
        // If we only have a start and user clicks again, make it end.
        if (from && !to) {
          setActiveRangeBoundary(null);
          return day < from ? { from: day, to: from } : { from, to: day };
        }
        setActiveRangeBoundary("to");
        return { from: day, to: undefined };
      }

      // If user clicked exactly a boundary, switch edit mode
      if (day.toDateString() === from.toDateString()) {
        setActiveRangeBoundary("from");
        return prev;
      }
      if (day.toDateString() === to.toDateString()) {
        setActiveRangeBoundary("to");
        return prev;
      }

      // If user is editing an existing range boundary
      if (activeRangeBoundary === "from") {
        setActiveRangeBoundary(null);
        return day <= to ? { from: day, to } : { from: to, to: day };
      }
      if (activeRangeBoundary === "to") {
        setActiveRangeBoundary(null);
        return day >= from ? { from, to: day } : { from: day, to: from };
      }

      // Default: clicking any other day starts a new selection
      setActiveRangeBoundary("to");
      return { from: day, to: undefined };
    });
  };

  const handleCustomPickerOpenChange = (open: boolean) => {
    setCustomPickerOpen(open);
    if (open) {
      // start editing from the currently applied range
      setDraftCustomRange(customRange);
      setActiveRangeBoundary(null);
      return;
    }
    // apply draft only when picker is closed → triggers API call via startDate/endDate
    setCustomRange(draftCustomRange);
    setActiveRangeBoundary(null);
  };

  const dateRangeToolbar = (
    <div className="flex flex-nowrap items-center gap-2 shrink-0">
      <Select
        value={datePreset}
        onValueChange={(v) => {
          const preset = v as ReportDatePreset;
          setDatePreset(preset);
          if (preset !== "custom") {
            setCustomPickerOpen(false);
            setActiveRangeBoundary(null);
          } else {
            handleCustomPickerOpenChange(true);
          }
        }}
      >
        <SelectTrigger className="w-[min(200px,42vw)] h-9 shrink-0 rounded-lg border-gray-300">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PRESET_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {datePreset === "custom" && (
        <Popover open={customPickerOpen} onOpenChange={handleCustomPickerOpenChange}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="h-9 shrink-0 rounded-lg whitespace-nowrap">
              {draftCustomRange?.from && draftCustomRange?.to
                ? `${draftCustomRange.from.toLocaleDateString()} — ${draftCustomRange.to.toLocaleDateString()}`
                : draftCustomRange?.from
                  ? `${draftCustomRange.from.toLocaleDateString()} — …`
                  : "Pick dates"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              numberOfMonths={2}
              selected={draftCustomRange}
              onSelect={handleCustomRangeSelect as any}
              defaultMonth={draftCustomRange?.from}
              disabled={(date) => isFutureCalendarDay(date)}
            />
          </PopoverContent>
        </Popover>
      )}
      <Button
        type="button"
        variant="default"
        size="sm"
        className="h-9 shrink-0 rounded-lg"
        onClick={resetAllReportFilters}
      >
        Reset
      </Button>
    </div>
  );

  const patientLikeFromCriticalRow = (row: any) => {
    if (row?.patientId && typeof row.patientId === "object") return row.patientId;
    if (row?.patient && typeof row.patient === "object") return row.patient;
    return row;
  };

  const formatReportAddress = (row: any): string => {
    if (!row || typeof row !== "object") return "";

    const direct = row.address ?? row.Address;
    if (typeof direct === "string" && direct.trim()) return direct.trim();

    const type = String(row.typeOfPatient ?? "")
      .trim()
      .toLowerCase();
    if (type === "outpatient" && typeof row.address === "string") {
      return row.address.trim();
    }
    if (type === "facility") {
      const orgAddr =
        row.organizationId?.address ??
        row.organizationId?.Address ??
        (typeof row.organizationId === "object"
          ? row.organizationId?.street
          : undefined);
      if (typeof orgAddr === "string" && orgAddr.trim()) return orgAddr.trim();
    }

    const parts = [row.street, row.city, row.state, row.zipCode]
      .map((part) => String(part ?? "").trim())
      .filter(Boolean);
    if (parts.length > 0) return parts.join(", ");

    return "";
  };

  /** Human-readable patient code for report table (`Patient ID` from API or nested patient). */
  const getCriticalReportPatientIdDisplay = (row: any): string => {
    const direct = row?.["Patient ID"];
    if (direct != null && typeof direct !== "object") {
      const s = String(direct).trim();
      if (s) return s;
    }
    if (direct != null && typeof direct === "object") {
      const o = direct as Record<string, unknown>;
      const fromObj = [o.patientId, o.patientCode, o._id].find(
        (v) => v != null && String(v).trim() !== "",
      );
      if (fromObj != null) return String(fromObj).trim();
    }
    const p = patientLikeFromCriticalRow(row);
    if (!p || typeof p !== "object") return "";
    if (typeof (p as any).patientId === "string")
      return String((p as any).patientId).trim();
    const nested = (p as any).patientId as Record<string, unknown> | undefined;
    if (nested && typeof nested === "object") {
      const code = nested.patientId ?? nested.patientCode ?? nested._id;
      if (code != null && String(code).trim()) return String(code).trim();
    }
    return "";
  };

  const getCriticalReportAddressDisplay = (row: any): string => {
    const direct = row?.Address ?? row?.address;
    if (typeof direct === "string" && direct.trim()) return direct.trim();

    const patient = patientLikeFromCriticalRow(row);
    const fromPatient = formatReportAddress(patient);
    if (fromPatient) return fromPatient;

    const orgAddr =
      typeof row?.organizationId === "object" && row.organizationId !== null
        ? row.organizationId.address ?? row.organizationId.Address
        : undefined;
    if (typeof orgAddr === "string" && orgAddr.trim()) return orgAddr.trim();

    return "";
  };

  /** Facility / Outpatient from `patientId.typeOfPatient` (badge via MyTable `typeOfPatient`). */
  const getCriticalReportPatientTypeDisplay = (row: any): string => {
    const patientId = row?.patientId;
    const raw =
      (patientId && typeof patientId === "object"
        ? patientId.typeOfPatient
        : undefined) ??
      row?.["Patient Type"] ??
      row?.patientType ??
      row?.typeOfPatient ??
      patientLikeFromCriticalRow(row)?.typeOfPatient;
    const t = String(raw ?? "")
      .trim()
      .toLowerCase()
      .replace(/_/g, " ");
    if (t === "facility") return "Facility";
    if (t === "outpatient") return "Outpatient";
    if (raw != null && String(raw).trim() !== "") return String(raw).trim();
    return "";
  };

  /**
   * `/critical-readings/report` returns flat rows: `timezone` is IANA (e.g. America/Chicago).
   * Header column id is `Timezone` — we set human-readable label there.
   */
  const getCriticalReportRowTimezoneDisplay = (row: any) => {
    if (!row) return "—";
    const direct =
      row.resolvedTimezone ??
      row.timezone ??
      row.timeZone ??
      row.Timezone;
    if (direct != null && String(direct).trim() !== "") {
      return formatOrganizationTimezoneDisplay(String(direct).trim());
    }
    const patient = patientLikeFromCriticalRow(row);
    const patientType = patient?.typeOfPatient;
    const orgOnRow = row.organizationId;
    const facOnRow = row.facilityId;

    if (patientType === "outpatient") {
      const iana =
        (typeof orgOnRow === "object" && orgOnRow !== null
          ? orgOnRow.timezone ?? orgOnRow.timeZone
          : undefined) ??
        (typeof patient?.organizationId === "object" && patient.organizationId !== null
          ? patient.organizationId.timezone ?? patient.organizationId.timeZone
          : undefined);
      return formatOrganizationTimezoneDisplay(iana);
    }
    if (patientType === "facility") {
      const iana =
        (typeof facOnRow === "object" && facOnRow !== null
          ? facOnRow.timezone ?? facOnRow.timeZone
          : undefined) ??
        (typeof patient?.facilityId === "object" && patient.facilityId !== null
          ? patient.facilityId.timezone ?? patient.facilityId.timeZone
          : undefined);
      return formatOrganizationTimezoneDisplay(iana);
    }

    if (typeof orgOnRow === "object" && orgOnRow !== null) {
      const iana = orgOnRow.timezone ?? orgOnRow.timeZone;
      if (iana) return formatOrganizationTimezoneDisplay(iana);
    }
    if (typeof facOnRow === "object" && facOnRow !== null) {
      const iana = facOnRow.timezone ?? facOnRow.timeZone;
      if (iana) return formatOrganizationTimezoneDisplay(iana);
    }

    return getPatientContextTimezoneDisplay(patient);
  };

  return (
    <DashboardLayout activePage="/Reports">
      <div className="space-y-4">
        <Tabs defaultValue="enrollment" className="w-full">
          <TabsList className="h-10 flex w-full md:w-fit rounded-lg bg-grey-light border border-grey-light p-0 overflow-hidden">
            <TabsTrigger
              value="enrollment"
              className="teachers text-sm px-4 flex-1 md:flex-none data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-none rounded-none"
            >
              Enrollment  Report
            </TabsTrigger>
            <TabsTrigger
              value="critical"
              className="teachers text-sm px-4 flex-1 md:flex-none data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-none rounded-none"
            >
              Critical Report
            </TabsTrigger>
          </TabsList>

          <TabsContent value="enrollment" className="mt-4">
            <TableWithSearch
              url="/patients/enrollment-report-list"
              universalLoadingSkeleton
              apiDataKey="patients"
              columnPreferenceKey={`org-report-patients-${id}`}
              columnHeaders={reportHeaders}
              data={enrollmentData}
              setData={setEnrollmentData}
              viewData={enrollmentData.map((v: any) => {
                const toDateOnly = (val: any) =>
                  val ? new Date(val).toISOString().split("T")[0] : val ?? "";
                return {
                  ...v,
                  dateOfBirth: toDateOnly(v?.dateOfBirth),
                  createdAt: toDateOnly(v?.createdAt),
                  updatedAt: toDateOnly(v?.updatedAt),
                  firstName: `${v?.firstName || ""} ${v?.middleName || ""} ${v?.lastName || ""}`,
                  organizationId: v?.organizationId?.organizationName,
                  enrolledBy: v?.enrolledBy?.name,
                  programs: v?.programs?.join(", "),
                  enrolledDate: toDateOnly(v?.enrolledDate) ?? "",
                  address: formatReportAddress(v),
                  deviceNames: (Array.isArray(v?.deviceNameList) && v?.deviceNameList?.length) && (
                    <HoverTooltip
                      content={
                        <div className="flex flex-col gap-1">
                          {v?.deviceNameList?.map((name: string, i: number) => (
                            <div key={i}>{i + 1}. {name}</div>
                          ))}
                        </div>
                      }
                    >
                      <button type="button" className="inline-flex items-center gap-1 cursor-pointer bg-transparent border-none p-0">
                        <span className="text-xs text-blue-500">{v?.deviceNameList?.length ?? 0}</span>
                      </button>
                    </HoverTooltip>
                  ),
                  assignedProviderId:
                    v?.assignedProviderId?.firstName ||
                    "" + " " + v?.assignedProviderId?.middleName ||
                    "" + " " + v?.assignedProviderId?.lastName ||
                    "",
                  assignedCaretakerIds: v?.assignedCaretakerIds
                    ?.map(
                      (c: any) =>
                        c?.firstName ||
                        "" + " " + c?.middleName ||
                        "" + " " + c?.lastName ||
                        "",
                    )
                    .join(", "),
                  heightValue: v?.heightValue?.includes("_")
                    ? `${v.heightValue.split("_")[0]}' ${v.heightValue.split("_")[1]}"`
                    : v?.heightValue,
                  patientTimezoneDisplay: getPatientContextTimezoneDisplay(v),
                  hasCCMCarePlan:
                    v?.hasCCMCarePlan === "yes"
                      ? "Yes"
                      : v?.hasCCMCarePlan === "no"
                        ? "No"
                        : v?.hasCCMCarePlan || "-",
                  hasPCMCarePlan:
                    v?.hasPCMCarePlan === "yes"
                      ? "Yes"
                      : v?.hasPCMCarePlan === "no"
                        ? "No"
                        : v?.hasPCMCarePlan || "-",
                  hasRPMCarePlan:
                    v?.hasRPMCarePlan === "yes"
                      ? "Yes"
                      : v?.hasRPMCarePlan === "no"
                        ? "No"
                        : v?.hasRPMCarePlan || "-",
                  ...(v?.caretakerMinutes ? v?.caretakerMinutes : {}),
                };
              })}
              fetchCondition={true}
              filters={{ ...enrollmentFilters, startDate, endDate }}
              setFilters={setEnrollmentFilters}
              filtersOptions={enrollmentFilterOptions}
              heading="Enrolment Report"
              headerExtraControls={dateRangeToolbar}
              isColumnSelection
              isDownloadData
              downloadAccessModule="canAccessReports"
              onDownload={handleEnrollmentExport}
              searchPlaceholder="Search patients..."
              clickRow={() => {}}
              isIncludeSearch
              params={omitOrganizationParam ? {} : { orgId: id }}
              isActionColumn={false}
              handleAction={() => {}}
              key={`enrollment-report-${id}-${startDate}-${endDate}`}
              isIgnoreSearchParams
              onClearAllFilters={resetAllReportFilters}
            />
          </TabsContent>

          <TabsContent value="critical" className="mt-4">
            <TableWithSearch
              heading="Critical Report"
              url="/critical-readings/report"
              universalLoadingSkeleton
              apiDataKey="rows"
              columnPreferenceKey={`org-critical-report-rows-${id}`}
              columnHeaders={criticalReportHeaders}
              data={criticalData}
              setData={setCriticalData}
              viewData={criticalData.map((row: any) => ({
                ...row,
                "Patient ID": getCriticalReportPatientIdDisplay(row),
                Address: getCriticalReportAddressDisplay(row),
                typeOfPatient: getCriticalReportPatientTypeDisplay(row),
                createdAt: row?.["Received At"] ? row?.["Received At"] : "",
                Timezone: getCriticalReportRowTimezoneDisplay(row),
              }))}
              fetchCondition={true}
              filters={{ ...criticalFilters, startDate, endDate }}
              setFilters={setCriticalFilters}
              filtersOptions={criticalFilterOptions}
              headerExtraControls={dateRangeToolbar}
              defaultFilters={{}}
              isIncludeSearch
              searchPlaceholder="Search Patient..."
              clickRow={() => {}}
              isActionColumn={false}
              handleAction={() => {}}
              params={omitOrganizationParam ? {} : { organizationId: id }}
              key={`critical-report-${id}-${startDate}-${endDate}`}
              isIgnoreSearchParams
              isDownloadData
              downloadAccessModule="canAccessReports"
              onDownload={handleCriticalExport}
              onClearAllFilters={resetAllReportFilters}
            />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default ReportPage;
